import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Grid,
  Box,
  Typography,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  LinearProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'
import {
  Schedule,
  Warning,
  CheckCircle,
  AccessTime,
  AttachMoney,
  TrendingUp,
  Person,
  CalendarMonth,
  ArrowForward,
  Print,
} from '@mui/icons-material'
import { Doughnut, Bar } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from 'chart.js'
import { PageHeader, StatCard, Card, LoadingSpinner, Button } from '@components/common'
import { formatDate, formatDateTime } from '@utils/dateUtils'
import api from '@services/api'

// Register ChartJS components
ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement)

// Format currency
const formatCurrency = (value) => {
  if (!value && value !== 0) return '-'
  return new Intl.NumberFormat('en-UG', {
    style: 'currency',
    currency: 'UGX',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)
}

const PMDashboard = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [dashboardData, setDashboardData] = useState(null)
  const [costForecast, setCostForecast] = useState([])
  const [technicianWorkload, setTechnicianWorkload] = useState([])
  const [forecastMonths, setForecastMonths] = useState(3)
  const [viewMode, setViewMode] = useState('dashboard')

  useEffect(() => {
    fetchDashboardData()
  }, [])

  useEffect(() => {
    fetchCostForecast()
  }, [forecastMonths])

  const fetchDashboardData = async () => {
    try {
      setLoading(true)
      const [dashboardRes, workloadRes] = await Promise.all([
        api.get('/maintenance/pm-schedules/dashboard/'),
        api.get('/maintenance/pm-schedules/technician_workload/'),
      ])
      setDashboardData(dashboardRes.data)
      setTechnicianWorkload(workloadRes.data)
    } catch (error) {
      console.error('Error fetching PM dashboard:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchCostForecast = async () => {
    try {
      const response = await api.get(`/maintenance/pm-schedules/cost_forecast/?months=${forecastMonths}`)
      setCostForecast(response.data)
    } catch (error) {
      console.error('Error fetching cost forecast:', error)
    }
  }

  if (loading) {
    return <LoadingSpinner message="Loading PM Dashboard..." />
  }

  const data = dashboardData || {}

  // Status distribution chart data
  const statusLabels = {
    scheduled: 'Scheduled',
    in_progress: 'In Progress',
    completed: 'Completed',
    overdue: 'Overdue',
    skipped: 'Skipped',
  }
  const statusColors = {
    scheduled: '#3B82F6',
    in_progress: '#F59E0B',
    completed: '#10B981',
    overdue: '#EF4444',
    skipped: '#6B7280',
  }

  const statusChartData = {
    labels: Object.keys(data.by_status || {}).map(k => statusLabels[k] || k),
    datasets: [{
      data: Object.values(data.by_status || {}),
      backgroundColor: Object.keys(data.by_status || {}).map(k => statusColors[k] || '#6B7280'),
      borderWidth: 0,
    }],
  }

  // Cost forecast chart data
  const costForecastChartData = {
    labels: costForecast.map(f => f.label),
    datasets: [
      {
        label: 'Labor Cost',
        data: costForecast.map(f => f.estimated_labor_cost),
        backgroundColor: '#3B82F6',
      },
      {
        label: 'Parts Cost',
        data: costForecast.map(f => f.estimated_parts_cost),
        backgroundColor: '#10B981',
      },
      {
        label: 'Actual Cost',
        data: costForecast.map(f => f.actual_cost),
        backgroundColor: '#F59E0B',
      },
    ],
  }

  // Technician workload chart data
  const workloadChartData = {
    labels: technicianWorkload.slice(0, 8).map(t => t.name.split(' ')[0]),
    datasets: [
      {
        label: 'Pending',
        data: technicianWorkload.slice(0, 8).map(t => t.pending),
        backgroundColor: '#3B82F6',
      },
      {
        label: 'In Progress',
        data: technicianWorkload.slice(0, 8).map(t => t.in_progress),
        backgroundColor: '#F59E0B',
      },
      {
        label: 'Completed',
        data: technicianWorkload.slice(0, 8).map(t => t.completed),
        backgroundColor: '#10B981',
      },
    ],
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          boxWidth: 12,
          padding: 15,
          font: { size: 11 },
        },
      },
    },
  }

  const barChartOptions = {
    ...chartOptions,
    scales: {
      x: { stacked: true },
      y: { stacked: true, beginAtZero: true },
    },
  }

  // Alerts
  const alerts = []
  if (data.overdue > 0) {
    alerts.push({ severity: 'error', message: `${data.overdue} PM schedules are overdue` })
  }
  if (data.due_this_week > 3) {
    alerts.push({ severity: 'warning', message: `${data.due_this_week} PMs due this week` })
  }

  return (
    <Box>
      <PageHeader
        title="PM Dashboard"
        subtitle="Preventive Maintenance overview and resource planning"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'PM Schedule', path: '/pm-schedule' },
          { label: 'Dashboard' },
        ]}
        actions={
          <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              startIcon={<Print />}
              onClick={() => navigate('/maintenance/pm-schedule/print')}
            >
              Print Schedule
            </Button>
            <Button variant="outlined" onClick={() => navigate('/pm-schedule')}>
              View Schedules
            </Button>
            <Button onClick={() => navigate('/pm-schedule?tab=gantt')}>
              Gantt View
            </Button>
          </Box>
        }
      />

      {/* Alerts */}
      {alerts.length > 0 && (
        <Box mb={3} display="flex" flexDirection="column" gap={1}>
          {alerts.map((alert, idx) => (
            <Alert key={idx} severity={alert.severity} variant="outlined">
              {alert.message}
            </Alert>
          ))}
        </Box>
      )}

      <Grid container spacing={3}>
        {/* Top Stats Row */}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Scheduled"
            value={data.total_scheduled || 0}
            icon={<Schedule />}
            color="primary"
            onClick={() => navigate('/pm-schedule')}
            subtitle="Pending PMs"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Due This Week"
            value={data.due_this_week || 0}
            icon={<CalendarMonth />}
            color="warning"
            onClick={() => navigate('/pm-schedule')}
            subtitle="Next 7 days"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Overdue"
            value={data.overdue || 0}
            icon={<Warning />}
            color="error"
            onClick={() => navigate('/pm-schedule')}
            subtitle="Requires attention"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Completed This Month"
            value={data.completed_this_month || 0}
            icon={<CheckCircle />}
            color="success"
            subtitle="This month"
          />
        </Grid>

        {/* Resource Requirements Row */}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Est. Hours This Month"
            value={`${data.total_estimated_hours || 0}h`}
            icon={<AccessTime />}
            color="info"
            subtitle="Total work hours"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Technicians Required"
            value={data.total_required_technicians || 0}
            icon={<Person />}
            color="primary"
            subtitle="Resource demand"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Active Technicians"
            value={technicianWorkload.filter(t => t.total_pms > 0).length}
            icon={<Person />}
            color="success"
            subtitle={`${technicianWorkload.length} available`}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Cost Variance"
            value={formatCurrency(
              (data.actual_cost_this_month || 0) - (data.estimated_cost_this_month || 0)
            )}
            icon={<TrendingUp />}
            color={
              (data.actual_cost_this_month || 0) > (data.estimated_cost_this_month || 0)
                ? 'error'
                : 'success'
            }
            subtitle={
              (data.actual_cost_this_month || 0) > (data.estimated_cost_this_month || 0)
                ? 'Over budget'
                : 'Under budget'
            }
          />
        </Grid>

        {/* Cost Breakdown Card */}
        <Grid item xs={12} md={6}>
          <Card title="Cost Breakdown - This Month">
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Estimated Costs
                </Typography>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">Labor:</Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {formatCurrency(data.estimated_labor_cost_this_month)}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">Parts:</Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {formatCurrency(data.estimated_parts_cost_this_month)}
                  </Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" fontWeight={600}>Total:</Typography>
                  <Typography variant="body2" fontWeight={600} color="primary.main">
                    {formatCurrency(data.estimated_cost_this_month)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Actual Costs
                </Typography>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">Labor:</Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {formatCurrency(data.actual_labor_cost_this_month)}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">Parts:</Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {formatCurrency(data.actual_parts_cost_this_month)}
                  </Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" fontWeight={600}>Total:</Typography>
                  <Typography variant="body2" fontWeight={600} color="success.main">
                    {formatCurrency(data.actual_cost_this_month)}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Card>
        </Grid>

        {/* Charts Row */}
        <Grid item xs={12} md={4}>
          <Card title="PM Status Distribution">
            <Box height={260}>
              {Object.keys(data.by_status || {}).length > 0 ? (
                <Doughnut data={statusChartData} options={chartOptions} />
              ) : (
                <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                  <Typography color="text.secondary">No data available</Typography>
                </Box>
              )}
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Card
            title="Cost Forecast"
            action={
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <Select
                  value={forecastMonths}
                  onChange={(e) => setForecastMonths(e.target.value)}
                >
                  <MenuItem value={3}>3 Months</MenuItem>
                  <MenuItem value={6}>6 Months</MenuItem>
                  <MenuItem value={12}>12 Months</MenuItem>
                </Select>
              </FormControl>
            }
          >
            <Box height={260}>
              {costForecast.length > 0 ? (
                <Bar data={costForecastChartData} options={barChartOptions} />
              ) : (
                <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                  <Typography color="text.secondary">No forecast data available</Typography>
                </Box>
              )}
            </Box>
          </Card>
        </Grid>

        {/* Technician Workload */}
        <Grid item xs={12} md={8}>
          <Card title="Technician Workload">
            <Box height={300}>
              {technicianWorkload.length > 0 ? (
                <Bar data={workloadChartData} options={barChartOptions} />
              ) : (
                <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                  <Typography color="text.secondary">No technicians assigned</Typography>
                </Box>
              )}
            </Box>
          </Card>
        </Grid>

        {/* Workload Details */}
        <Grid item xs={12} md={4}>
          <Card title="Technician Details">
            <Box maxHeight={300} overflow="auto">
              {technicianWorkload.length > 0 ? (
                <List dense disablePadding>
                  {technicianWorkload.map((tech, index) => (
                    <Box key={tech.id}>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemText
                          primary={tech.name}
                          secondary={`${tech.total_hours} hours scheduled`}
                          primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                        <Box textAlign="right">
                          <Typography variant="body2" fontWeight={600}>
                            {tech.total_pms} PMs
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(tech.utilization_percent, 100)}
                            color={
                              tech.utilization_percent > 100
                                ? 'error'
                                : tech.utilization_percent > 80
                                ? 'warning'
                                : 'primary'
                            }
                            sx={{ width: 60, height: 4, borderRadius: 2, mt: 0.5 }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {tech.utilization_percent}% util.
                          </Typography>
                        </Box>
                      </ListItem>
                      {index < technicianWorkload.length - 1 && <Divider />}
                    </Box>
                  ))}
                </List>
              ) : (
                <Box py={4} textAlign="center">
                  <Typography color="text.secondary">No technicians found</Typography>
                </Box>
              )}
            </Box>
          </Card>
        </Grid>

        {/* Template Distribution */}
        <Grid item xs={12} md={6}>
          <Card title="PM by Template">
            {data.by_template?.length > 0 ? (
              <List dense disablePadding>
                {data.by_template.map((template, index) => (
                  <Box key={index}>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemText
                        primary={template.template__name || 'No Template'}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                      <Box display="flex" alignItems="center" gap={2}>
                        <LinearProgress
                          variant="determinate"
                          value={
                            (template.count / Math.max(...data.by_template.map(t => t.count))) * 100
                          }
                          sx={{ width: 100, height: 6, borderRadius: 3 }}
                        />
                        <Typography variant="body2" fontWeight={600} sx={{ minWidth: 30 }}>
                          {template.count}
                        </Typography>
                      </Box>
                    </ListItem>
                    {index < data.by_template.length - 1 && <Divider />}
                  </Box>
                ))}
              </List>
            ) : (
              <Box py={4} textAlign="center">
                <Typography color="text.secondary">No template data</Typography>
              </Box>
            )}
          </Card>
        </Grid>

        {/* Upcoming PMs */}
        <Grid item xs={12} md={6}>
          <Card
            title="Upcoming PMs"
            action={
              <Chip
                label="View All"
                size="small"
                onClick={() => navigate('/pm-schedule')}
                sx={{ cursor: 'pointer' }}
              />
            }
          >
            <Box maxHeight={300} overflow="auto">
              {data.upcoming?.length > 0 ? (
                <List dense disablePadding>
                  {data.upcoming.slice(0, 10).map((pm, index) => (
                    <Box key={pm.id}>
                      <ListItem
                        sx={{
                          px: 0,
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: '#f8fafc' },
                          borderRadius: 1,
                        }}
                        onClick={() => navigate('/pm-schedule')}
                      >
                        <ListItemText
                          primary={
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography variant="body2" fontWeight={500}>
                                {pm.template_name}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                              <Typography variant="caption" color="text.secondary">
                                {pm.asset_name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                |
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Due: {formatDate(pm.due_date)}
                              </Typography>
                            </Box>
                          }
                        />
                        <Box textAlign="right">
                          <Typography variant="caption" color="primary.main">
                            {pm.assigned_to || 'Unassigned'}
                          </Typography>
                          {pm.estimated_cost > 0 && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              {formatCurrency(pm.estimated_cost)}
                            </Typography>
                          )}
                        </Box>
                      </ListItem>
                      {index < Math.min(data.upcoming.length, 10) - 1 && <Divider />}
                    </Box>
                  ))}
                </List>
              ) : (
                <Box py={4} textAlign="center">
                  <CheckCircle color="success" sx={{ fontSize: 32, mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    No upcoming PMs in the next 30 days
                  </Typography>
                </Box>
              )}
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Box>
  )
}

export default PMDashboard
